import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { customerService } from '../services/customer.service'

export const customerQueryKeys = {
  dashboard: ['customer', 'dashboard'] as const,
  approvalStatus: ['customer', 'approval-status'] as const,
  subscription: ['customer', 'subscription'] as const,
  permissions: ['customer', 'permissions'] as const,
  history: ['customer', 'approval-history'] as const,
}

export function useCustomerDashboard() {
  return useQuery({
    queryKey: customerQueryKeys.dashboard,
    queryFn: customerService.getDashboard,
  })
}

export function useCustomerApprovalStatus() {
  return useQuery({
    queryKey: customerQueryKeys.approvalStatus,
    queryFn: customerService.getApprovalStatus,
  })
}

export function useCustomerSubscription() {
  return useQuery({
    queryKey: customerQueryKeys.subscription,
    queryFn: customerService.getSubscription,
  })
}

export function useCustomerPermissions() {
  return useQuery({
    queryKey: customerQueryKeys.permissions,
    queryFn: customerService.getPermissions,
  })
}

export function useCustomerSubmissionHistory() {
  return useQuery({
    queryKey: customerQueryKeys.history,
    queryFn: customerService.getSubmissionHistory,
  })
}

export function useCustomerFormConfig(formType: 'SELLER' | 'DEALER') {
  return useQuery({
    queryKey: ['customer', 'form-config', formType],
    queryFn: () => customerService.getFormConfig(formType),
  })
}

export function usePublicPlans() {
  return useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: customerService.getPublicPlans,
  })
}

export function useSubmitSellerRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: any) => customerService.submitSellerRequest(payload),
    onSuccess: () => {
      toast.success('Seller application submitted successfully! Platform admin will review your request.')
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.history })
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.approvalStatus })
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit seller application.')
    },
  })
}

export function useSubmitDealerRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: any) => customerService.submitDealerRequest(payload),
    onSuccess: () => {
      toast.success('Dealer application submitted successfully! Admin will verify your business credentials.')
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.history })
      queryClient.invalidateQueries({ queryKey: customerQueryKeys.approvalStatus })
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit dealer application.')
    },
  })
}
